import { useCallback, useMemo, useState } from 'react';
import { View, StyleSheet, ActivityIndicator } from 'react-native';
import { WebView, type WebViewMessageEvent } from 'react-native-webview';

/**
 * Wrapper Turnstile via WebView pra Android/iOS nativo.
 *
 * Carrega https://arafit.com.br/turnstile.html que renderiza o widget
 * Cloudflare e envia o token de volta via window.ReactNativeWebView.postMessage.
 * Workaround pra falta de SDK oficial Turnstile pra React Native — vide Radar [015].
 *
 * Props:
 *   - siteKey: chave publica do widget Turnstile (EXPO_PUBLIC_TURNSTILE_SITE_KEY)
 *   - onToken: callback com o token quando user resolve o captcha
 *   - onError: callback opcional pra falhas (widget_error, script_load_timeout, etc)
 *
 * Hostname `arafit.com.br` (sem www) DEVE estar registrado no widget Turnstile no
 * Cloudflare Dashboard. Decisao 2026-06-02 (Hadaiury): registrar apenas raw, sem www.
 *
 * Quando usar: na tela de login mobile (Platform.OS !== 'web').
 * No web, usar o widget oficial via script direto (vide login.tsx).
 */
const TURNSTILE_HTML_URL = 'https://arafit.com.br/turnstile.html';

type TurnstileMessage =
  | { type: 'turnstile_ready' }
  | { type: 'turnstile_token'; token: string }
  | { type: 'turnstile_expired' }
  | { type: 'turnstile_error'; reason?: string; detail?: string };

interface Props {
  siteKey: string;
  onToken: (token: string) => void;
  onError?: (reason?: string) => void;
}

export function TurnstileWebView({ siteKey, onToken, onError }: Props) {
  const [ready, setReady] = useState(false);

  const uri = useMemo(
    () => `${TURNSTILE_HTML_URL}?sitekey=${encodeURIComponent(siteKey)}`,
    [siteKey],
  );

  const handleMessage = useCallback(
    (event: WebViewMessageEvent) => {
      let msg: TurnstileMessage | null = null;
      try {
        msg = JSON.parse(event.nativeEvent.data) as TurnstileMessage;
      } catch {
        return;
      }
      if (!msg || typeof msg !== 'object') return;
      switch (msg.type) {
        case 'turnstile_ready':
          setReady(true);
          break;
        case 'turnstile_token':
          if (msg.token) onToken(msg.token);
          break;
        case 'turnstile_expired':
        case 'turnstile_error':
          onError?.((msg as { reason?: string }).reason);
          break;
      }
    },
    [onToken, onError],
  );

  return (
    <View style={styles.container}>
      {!ready && (
        <View style={styles.loadingOverlay} pointerEvents="none">
          <ActivityIndicator color="#C8102E" />
        </View>
      )}
      <WebView
        source={{ uri }}
        onMessage={handleMessage}
        style={styles.webview}
        scalesPageToFit={false}
        javaScriptEnabled
        domStorageEnabled
        startInLoadingState={false}
        // Permite o WebView ser transparente (background do app aparece)
        androidLayerType="hardware"
        // Evita o usuario fazer scroll horizontal/vertical dentro do iframe
        scrollEnabled={false}
        bounces={false}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: 320,
    height: 75,
    alignSelf: 'center',
    marginVertical: 8,
    backgroundColor: 'transparent',
  },
  webview: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
